// src/index.js
import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import "./index.css";
import App from "./App";
import reportWebVitals from "./reportWebVitals";
import { queryClient } from "./queryClient";
import * as Sentry from "@sentry/react";

// Initialize Sentry BEFORE React renders
Sentry.init({
  dsn: process.env.REACT_APP_SENTRY_DSN,
  environment: process.env.REACT_APP_SENTRY_ENVIRONMENT || "development",

  // Enable structured logging
  enableLogs: true,

  // Integrations
  integrations: [
    // Performance monitoring
    Sentry.browserTracingIntegration(),

    // Session replay - see what user was doing when error occurred
    Sentry.replayIntegration({
      maskAllText: true, // Privacy: mask all text
      blockAllMedia: true, // Privacy: block all media
    }),

    // Automatic console logging to Sentry
    Sentry.consoleLoggingIntegration({
      levels: ["log", "warn", "error"],
    }),
  ],

  // Performance Monitoring
  tracesSampleRate: 1.0, // Capture 100% of transactions

  // Session Replay
  replaysSessionSampleRate: 0.1, // 10% of normal sessions
  replaysOnErrorSampleRate: 1.0, // 100% of sessions with errors

  // Filter errors in development (optional)
  beforeSend(event, hint) {
    // Only send in production OR if explicitly enabled in development
    if (
      process.env.NODE_ENV === "development" &&
      process.env.REACT_APP_SENTRY_ENVIRONMENT !== "development"
    ) {
      return null;
    }
    return event;
  },

  // Ignore common non-errors
  ignoreErrors: [
    // Browser extensions
    "top.GLOBALS",
    "originalCreateNotification",
    "canvas.contentDocument",
    "MyApp_RemoveAllHighlights",
    "fb_xd_fragment",
    "bmi_SafeAddOnload",
    "EBCallBackMessageReceived",
    // Chrome/Firefox extensions
    "chrome-extension://",
    "moz-extension://",
    // Harmless ResizeObserver errors
    "ResizeObserver loop limit exceeded",
    "ResizeObserver loop completed with undelivered notifications",
  ],

  // Only track errors from your domain
  allowUrls: [/my\.redsetterhome\.com/, /localhost/],
});

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <App /> {/* DevTools only shows in development */}
      {window.innerWidth > 768 && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  </React.StrictMode>
);

reportWebVitals();
